/**
 * The result of a Gen 4 solenoid actuation check, decoded from bits 4-7 of the valve-pulse byte (see
 * `decodeGen4ValvePulse` in the backend).
 *
 * Gen 4 only — Sprout and Sapling have no onboard solenoid verification signal — so unlike
 * `OutputOperatingMode` there's no generation-agnostic renaming story here. The backend imports this
 * enum directly rather than keeping its own copy in sync.
 */
export enum SolenoidVerificationResult {
    SUCCESS = 0,
    FAIL_OPEN_CIRCUIT = 1,
    FAIL_SHORT_CIRCUIT = 2,
    FAIL_PIN_STUCK = 3,
}

const ALL_RESULTS: readonly SolenoidVerificationResult[] = [
    SolenoidVerificationResult.SUCCESS,
    SolenoidVerificationResult.FAIL_OPEN_CIRCUIT,
    SolenoidVerificationResult.FAIL_SHORT_CIRCUIT,
    SolenoidVerificationResult.FAIL_PIN_STUCK,
];

/** The inclusive range of legal stored values, for the sensor element's `legalConvertedRange`. */
export const SOLENOID_VERIFICATION_RESULT_RANGE: [number, number] = [
    SolenoidVerificationResult.SUCCESS,
    SolenoidVerificationResult.FAIL_PIN_STUCK,
];

/**
 * Narrows a stored value to the enum, or `null` if a device reports a value this build doesn't
 * recognize.
 *
 * The raw field is 4 bits wide (0-15 on the wire), so future firmware could report a code beyond the
 * four declared today; `null` lets a chart show "unknown" instead of mislabelling it as SUCCESS.
 */
export function parseSolenoidVerificationResult(value: number): SolenoidVerificationResult | null {
    return ALL_RESULTS.includes(value as SolenoidVerificationResult) ? (value as SolenoidVerificationResult) : null;
}

/**
 * The i18n key for displaying a result. Kept next to the enum so that adding a result without giving
 * it a label is a compile error rather than a blank chip in the UI.
 */
export function solenoidVerificationResultLabelKey(result: SolenoidVerificationResult): string {
    switch (result) {
        case SolenoidVerificationResult.SUCCESS:
            return "solenoidVerification.success";
        case SolenoidVerificationResult.FAIL_OPEN_CIRCUIT:
            return "solenoidVerification.openCircuit";
        case SolenoidVerificationResult.FAIL_SHORT_CIRCUIT:
            return "solenoidVerification.shortCircuit";
        case SolenoidVerificationResult.FAIL_PIN_STUCK:
            return "solenoidVerification.pinStuck";
    }
}
